// DetailedOrderCard.js
// Reusable details popup for Cash Payments and Refunds.
// - Cash variant: shows amount tendered (currency input, 2 decimals), live change = tendered - total, Paid button enabled only when change >= 0
// - Refund variant: shows refund details with a single REFUNDED button

import React, { useMemo, useState, useEffect } from "react";
import { Button, Row, Col, Input, Label } from "reactstrap";

function get(obj, path, def) {
  // Simple safe getter without optional chaining
  try {
    const parts = Array.isArray(path) ? path : String(path).split(".");
    let cur = obj;
    for (let i = 0; i < parts.length; i++) {
      if (cur == null) return def;
      cur = cur[parts[i]];
    }
    return cur == null ? def : cur;
  } catch (_) {
    return def;
  }
}

function toCurrencyString(v) {
  if (v === "" || v == null || isNaN(v)) return "";
  const n = Number(v);
  return n.toFixed(2);
}

function sanitizeCurrencyInput(s) {
  // Allow digits and one dot, limit to 2 decimals
  if (typeof s !== "string") s = String(s || "");
  // Remove invalid chars
  s = s.replace(/[^0-9.]/g, "");
  // Keep only first dot
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot + 1);
    const after = s.slice(firstDot + 1).replace(/\./g, "");
    s = before + after;
  }
  // Limit to 2 decimals
  if (firstDot !== -1) {
    const [head, tail] = s.split(".");
    s = head + "." + tail.slice(0, 2);
  }
  // Trim leading zeros unless decimals present
  if (s !== "" && s !== ".") {
    // avoid stripping single zero before decimal like 0.5
    if (s.indexOf(".") === -1) {
      // integer value
      s = String(parseInt(s || "0", 10));
    } else {
      const [h, t] = s.split(".");
      const hNum = isNaN(parseInt(h || "0", 10)) ? "0" : String(parseInt(h || "0", 10));
      s = hNum + "." + t;
    }
  }
  return s;
}

export default function DetailedCashOrderCard(props) {
  // Accept either a GraphQL edge or a direct order object
  const orderNode = useMemo(() => {
    const maybeEdge = props.order;
    if (maybeEdge && maybeEdge.node) return maybeEdge.node;
    return maybeEdge || {};
  }, [props.order]);

  // Infer variant if not provided
  const variant = useMemo(() => {
    if (props.variant === "cash" || props.variant === "refund") return props.variant;
    // Infer: presence of refundAmount => refund; else cash
    const refundAmount = get(orderNode, "refundAmount", null);
    return refundAmount != null ? "refund" : "cash";
  }, [props.variant, orderNode]);

  // Common fields
  const masterOrderId = String(get(orderNode, "masterOrderId", ""));
  const foodCourtId = String(get(orderNode, "foodCourtId", ""));
  const token = String(get(orderNode, "token", ""));

  // Cash-specific
  const totalAmount = Number(get(orderNode, "totalAmount", NaN));
  const mode = String(get(orderNode, "mode", "Cash"));

  // Refund-specific
  const refundAmount = Number(get(orderNode, "refundAmount", NaN));
  const subOrderId = String(get(orderNode, "subOrderId", ""));
  const storeId = String(get(orderNode, "storeId", ""));
  const storeName = String(get(orderNode, "storeName", ""));
  const orderStatus = String(get(orderNode, "status", "NEW")).toUpperCase();

  const [tenderedStr, setTenderedStr] = useState("");
  const [changeVal, setChangeVal] = useState(NaN);

  // derive status (default NEW) and compute which buttons to show

  const isPaid = orderStatus === "PAID";
  const isRefunded = orderStatus === "REFUNDED";
  const statusLabel = orderStatus === "NEW" ? "PENDING" : orderStatus;

  // show receipt if server says PAID/REFUNDED or parent forces it
  const showReceipt = isPaid || isRefunded || props.forceShowReceipt === true;

  useEffect(() => {
    if (variant === "cash") {
      const tendered = tenderedStr === "" ? NaN : Number(tenderedStr);
      const change = !isNaN(tendered) && !isNaN(totalAmount) ? tendered - totalAmount : NaN;
      setChangeVal(change);
    } else {
      setChangeVal(NaN);
    }
  }, [tenderedStr, totalAmount, variant]);

  const canPay = useMemo(() => {
    if (variant !== "cash") return false;
    if (isNaN(totalAmount)) return false;
    if (tenderedStr === "") return false;
    if (orderStatus != "NEW") return false;
    const tendered = Number(tenderedStr);
    if (isNaN(tendered)) return false;
    return tendered - totalAmount >= 0;
  }, [variant, totalAmount, tenderedStr]);
 
const canRefund = useMemo(() => {
  if (variant !== "refund") return false;
  if (isNaN(refundAmount)) return false;
  if (orderStatus != "NEW") return false;
  return true;
}, [variant, refundAmount, orderStatus]);

  function onPaidClick() {
   
    if (!canPay) return;
    const tendered = Number(tenderedStr);
    const payload = {
      orderId:masterOrderId,
      masterOrderId: masterOrderId,
      totalAmount: totalAmount,
      foodCourtId: foodCourtId,
      token: token,
      mode: mode,
      tendered_amount: Number(toCurrencyString(tendered)),
      changed_tendered: Number(toCurrencyString(tendered - totalAmount)),
      variant: "cash",
       status:"PAID",
      order: orderNode,
    };
     console.log('inside onpaidClick')
    if (typeof props.onPaid === "function") props.onPaid(payload);
     console.log('inside onpaidClick-1')
  }

  function onRefundedClick() {
   
    const payload = {
      orderId:subOrderId,
      masterOrderId: masterOrderId,
      foodCourtId: foodCourtId,
      token: token,
      subOrderId: subOrderId,
      refundAmount: refundAmount,
      storeId: storeId,
      storeName: storeName,
      variant: "refund",
      status:"REFUNDED",
      order: orderNode,
    };
    if (typeof props.onRefunded === "function") props.onRefunded(payload);
  }


    function onPAIDPrintClick() {
    
    const payload = {
      order_id:masterOrderId
    };
    console.log('payload',payload);
    if (typeof props.onRePrint === "function") props.onRePrint(payload);
  }
   function onREFUNDPrintClick() {
    
    const payload = {
      order_id:subOrderId
    };
    console.log('payload',payload);
    if (typeof props.onRePrint === "function") props.onRePrint(payload);
  }


  function renderRow(label, value) {
    return (
      <Row style={{ marginBottom: 8 }}>
        <Col xs="5"><strong>{label}</strong></Col>
        <Col xs="7" style={{ wordBreak: "break-all" }}>{value}</Col>
      </Row>
    );
  }

  return (
   
<div style={{ padding: 16 }}>
  <h5 style={{ marginBottom: 12 }}>
    {variant === "cash" ? "Cash Payment" : "Refund"} — Details
  </h5>

  {renderRow("Master Order ID", masterOrderId)}
  {variant === "cash" ? renderRow("Total Amount", isNaN(totalAmount) ? "" : toCurrencyString(totalAmount)) : null}
  {renderRow("Token", token)}
  {variant === "cash" ? renderRow("Mode", mode || "Cash") : null}
  {renderRow("Status", statusLabel)}

  {/* CASH VARIANT: always render the section, but only show inputs when NEW */}
  {variant === "cash" ? (
    <div style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 8 }}>
      {orderStatus === "NEW" && (
        <div>
          <Row style={{ marginBottom: 8 }}>
            <Col xs="5"><Label><strong>Amount Tendered</strong></Label></Col>
            <Col xs="7">
              <Input
                value={tenderedStr}
                placeholder="0.00"
                onChange={(e) => {
                  const next = sanitizeCurrencyInput(e.target.value);
                  setTenderedStr(next);
                }}
                onBlur={() => setTenderedStr(toCurrencyString(tenderedStr))}
                inputMode="decimal"
                pattern="^\\d+(?:\\.\\d{0,2})?$"
                title="Enter a valid amount (up to 2 decimals)"
              />
            </Col>
          </Row>
          <Row style={{ marginBottom: 12 }}>
            <Col xs="5"><strong>Change</strong></Col>
            <Col xs="7">{isNaN(changeVal) ? "" : toCurrencyString(changeVal)}</Col>
          </Row>
        </div>
      )}

      {/* Footer buttons for CASH: Close always visible; show Receipt if PAID/REFUNDED; else show Paid */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button color="secondary" onClick={props.goBack}>Close</Button>

        {showReceipt && (
          <Button color="primary" onClick={onPAIDPrintClick}>Receipt</Button>
        )}

        {!showReceipt && orderStatus === "NEW" && (
          <Button
            style={{ backgroundColor: canPay ? "green" : "red", borderColor: canPay ? "green" : "red", color: "white" }}
            disabled={!canPay}
            onClick={onPaidClick}
          >
            Paid
          </Button>
        )}
      </div>
    </div>
  ) : null}

  {/* REFUND VARIANT */}
  {variant === "refund" ? (
    <div style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 8 }}>
      {renderRow("Sub Order ID", subOrderId)}
      {renderRow("Refund Amount", isNaN(refundAmount) ? "" : toCurrencyString(refundAmount))}
      {renderRow("Store ID", storeId)}
      {renderRow("Store Name", storeName)}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <Button color="secondary" onClick={props.goBack}>Close</Button>

        {showReceipt && (
          <Button color="primary" onClick={onREFUNDPrintClick}>Receipt</Button>
        )}

        {!showReceipt && orderStatus === "NEW" && (
          <Button
            style={{ backgroundColor: canRefund ? "green" : "red", borderColor: canRefund ? "green" : "red", color: "white" }}
            onClick={onRefundedClick}
          >
            REFUND
          </Button>
        )}
      </div>
    </div>
  ) : null}
</div>
  );
}



// WEBPACK FOOTER //
// ./src/components/DetailedOrderCard/DetailedCashOrderCard.js